import { useMemo } from "react";
import { View, ScrollView } from "react-native";
import {
  Typography,
  DrawerContent,
  DrawerActions,
  Button,
  VStack,
  ActivityIndicator,
  Accordion,
  AccordionPanel,
  useAccordionState,
  useSelectQuery,
} from "@smartrent/ui";
import { FormMultiSelectField } from "@smartrent/forms";
import { PaginatedResponse } from "@smartrent/hooks";
import * as Yup from "yup";

import { instance } from "@/lib/hooks";
import { useGroupsQuery } from "@/api/organization";
import { GroupProps } from "@/types";

import { useCMWFeatureFlagDetails } from "../hooks/useCMWFeatureFlagDetails";
import { CMWFeatureFlagState } from "../types";

import { CMWFlagListItem } from "./CMWFlagListItem";
import { CMWFeatureFlagForm } from "./CMWFeatureFlagForm";
import { sharedStyles } from "./styles";

interface GroupWithFlags {
  groupId: number;
  groupName: string;
  flags: Array<CMWFeatureFlagState>;
}

export function CMWGroupFlagsDetails({
  organizationId,
  organizationName,
}: {
  organizationId: number;
  organizationName: string;
}) {
  const {
    isAddingFlag,
    hierarchyData,
    hierarchyLoading,
    flagOptions,
    handleAddFlag,
    handleCancelAdd,
    handleToggleFlag,
    handleRemoveFlag,
    handleSaveFlag,
  } = useCMWFeatureFlagDetails("group");

  const accordionProps = useAccordionState({
    allowMultiple: true,
  });

  const { data: groups, isLoading: groupsLoading } = useGroupsQuery(
    organizationId,
    {}
  );

  const groupSelectQueryProps = useSelectQuery<PaginatedResponse<GroupProps>>(
    (inputValue) => ["groups-select", { inputValue, organizationId }] as const,
    function fetch({ queryKey, pageParam = 1 }) {
      const [, params] = queryKey;
      const inputValue = (
        params as { inputValue?: string; organizationId: number }
      )?.inputValue;
      const orgId = (params as { inputValue?: string; organizationId: number })
        ?.organizationId;
      return instance
        .get(`/groups`, {
          params: {
            marketing_name: inputValue ? inputValue : undefined,
            organization_id: orgId,
            active: true,
            page: pageParam,
            limit: 25,
          },
        })
        .then((response) => response.data);
    },
    {},
    {
      inputDebounceInterval: 250,
    }
  );

  const groupsWithFlags = useMemo(() => {
    if (!hierarchyData || !groups) {
      return [];
    }

    const orgIdStr = organizationId.toString();
    const orgData = hierarchyData.organizations[orgIdStr];
    const allData = hierarchyData.organizations["all"];

    if (!orgData) {
      return [];
    }

    const orgGroupFlags = orgData.group_level_flags || [];
    const allGroupFlags = allData?.group_level_flags || [];

    const groupFlagsMap = orgGroupFlags.reduce((map, flag) => {
      const groupId = flag.actor_id;
      if (!map.has(groupId)) {
        map.set(groupId, []);
      }
      map.get(groupId)!.push({
        enabled: flag.enabled,
        name: flag.name,
      });
      return map;
    }, new Map<number, Array<CMWFeatureFlagState>>());

    // Merge flags from "all" organizations into the map, avoiding duplicates
    allGroupFlags.forEach((flag) => {
      const groupId = flag.actor_id;
      if (!groupFlagsMap.has(groupId)) {
        groupFlagsMap.set(groupId, []);
      }
      const existingFlags = groupFlagsMap.get(groupId)!;
      const existingFlagNames = new Set(existingFlags.map((f) => f.name));
      if (!existingFlagNames.has(flag.name)) {
        existingFlags.push({
          enabled: flag.enabled,
          name: flag.name,
        });
      }
    });

    const groupsArray = Array.isArray(groups) ? groups : [];
    const groupMap = groupsArray.reduce(
      (map: Map<number, string>, group: GroupProps) => {
        map.set(Number(group.id), group.marketing_name);
        return map;
      },
      new Map<number, string>()
    );

    const result: GroupWithFlags[] = Array.from(groupFlagsMap.entries())
      .map(([groupId, flags]) => {
        const groupName = groupMap.get(groupId);
        if (!groupName) {
          return null;
        }
        return {
          groupId,
          groupName,
          flags: flags.sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          ),
        };
      })
      .filter((item): item is GroupWithFlags => item !== null)
      .sort((a, b) => a.groupName.localeCompare(b.groupName));

    return result;
  }, [hierarchyData, groups, organizationId]);

  const handleSaveNewFlag = async (values: {
    featureFlag: string;
    enabled: boolean;
    groupIds: number[];
  }) => {
    for (const groupId of values.groupIds) {
      await handleSaveFlag(
        {
          featureFlag: values.featureFlag,
          enabled: values.enabled,
        },
        groupId,
        organizationId
      );
    }
  };

  const isLoading = hierarchyLoading || groupsLoading;

  if (isLoading) {
    return (
      <View style={sharedStyles.loadingContainer}>
        <ActivityIndicator size={24} />
      </View>
    );
  }

  const formContent = isAddingFlag ? (
    <CMWFeatureFlagForm
      title={`Group Flags for ${organizationName}`}
      flagOptions={flagOptions}
      initialValues={{
        groupIds: [] as number[],
        featureFlag: "",
        enabled: false,
      }}
      validationSchema={Yup.object({
        groupIds: Yup.array()
          .of(Yup.number())
          .min(1, "You must select at least one group")
          .required("You must select at least one group"),
        featureFlag: Yup.string().required("You must select a feature flag"),
      })}
      onSubmit={handleSaveNewFlag}
      onCancel={handleCancelAdd}
      enabledAssistiveText="Whether the feature is enabled or not for this group"
      additionalFields={
        <FormMultiSelectField<GroupProps>
          {...groupSelectQueryProps}
          name="groupIds"
          filtering={true}
          label="Select Groups"
          getOptionValue={(option: GroupProps) => Number(option.id)}
          getOptionLabel={(option: GroupProps) => option.marketing_name}
          required
        />
      }
    />
  ) : (
    <>
      <DrawerContent>
        <ScrollView>
          <View style={sharedStyles.container}>
            <Typography type="title3">
              Group Flags for {organizationName}
            </Typography>
            <VStack spacing={16} style={sharedStyles.content}>
              {groupsWithFlags.length === 0 ? (
                <Typography color="textDisabled">
                  No groups with flags found
                </Typography>
              ) : (
                <Accordion {...accordionProps} truncate={false}>
                  {groupsWithFlags.map((group) => (
                    <AccordionPanel
                      key={group.groupId}
                      title={`${group.groupName} (${group.flags.length} flag${group.flags.length !== 1 ? "s" : ""})`}
                    >
                      <VStack spacing={16}>
                        {group.flags.length === 0 ? (
                          <Typography color="textDisabled">
                            No group flags
                          </Typography>
                        ) : (
                          <View style={sharedStyles.flagsList}>
                            {group.flags.map((flag, idx) => (
                              <CMWFlagListItem
                                key={flag.name}
                                flagName={flag.name}
                                enabled={flag.enabled}
                                onToggle={() =>
                                  handleToggleFlag(
                                    flag.name,
                                    flag.enabled,
                                    group.groupId,
                                    organizationId
                                  )
                                }
                                onRemove={() =>
                                  handleRemoveFlag(flag.name, group.groupId)
                                }
                                showSeparator={idx < group.flags.length - 1}
                                switchName={`toggle-${flag.name}-${group.groupId}`}
                              />
                            ))}
                          </View>
                        )}
                      </VStack>
                    </AccordionPanel>
                  ))}
                </Accordion>
              )}
            </VStack>
          </View>
        </ScrollView>
      </DrawerContent>
      <DrawerActions>
        <Button onPress={handleAddFlag} style={sharedStyles.fullWidthButton}>
          Add Group Flag
        </Button>
      </DrawerActions>
    </>
  );

  return formContent;
}
